import React, { useState } from "react";
import { db } from "../firebaseConfig";
import { doc, updateDoc, serverTimestamp, addDoc, getDoc, collection } from "firebase/firestore";

const HoldModal = ({ item, user, setShowModal, setInventory }) => {
  const [model, setModel] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState(null); // Add this line

  const handleSubmit = async () => {
    try {
      const isBackdoor = item.id.endsWith("__backdoor");
      const ref = doc(db, "inventory", item.id);

      // Fetch user info
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const fullName = `${userDoc.data()?.firstName || ""} ${userDoc.data()?.lastName || ""}`.trim();

      // Fetch the latest inventory state to avoid stale data
      const inventoryDoc = await getDoc(ref);
      const currentData = inventoryDoc.data() || {};
      const currentVisible = currentData.visible || 0;
      const currentBackdoor = currentData.backdoor || 0;
      const currentOnHold = currentData.onHold || 0;

      // Calculate the new values
      const newVisible = isBackdoor ? currentVisible : Math.max(0, currentVisible - 1);
      const newBackdoor = isBackdoor ? Math.max(0, currentBackdoor - 1) : currentBackdoor;
      const newOnHold = currentOnHold + 1;

      // Create the onHold document
      const holdDocRef = await addDoc(collection(db, "onHold"), {
        originalItemId: item.id,
        originalSection: item.section || (isBackdoor ? "backdoor" : "Unknown"),
        brand: item.name || "Unknown",
        model: model.trim() || "Unknown",
        reason: reason.trim() || "No reason provided",
        holdBy: fullName || user.email,
        holdDate: serverTimestamp(),
        lastUpdated: serverTimestamp(),
      });

      // Add the initial comment (if provided)
      if (model.trim() || reason.trim()) {
        const commentRef = collection(db, "onHold", holdDocRef.id, "comments");
        await addDoc(commentRef, {
          user: fullName || user.email,
          message: `Model: ${model.trim() || "Unknown"}, Reason: ${reason.trim() || "No reason provided"}`,
          timestamp: serverTimestamp(),
        });
      }

      // Update the inventory item
      await updateDoc(ref, {
        visible: newVisible,
        backdoor: newBackdoor,
        onHold: newOnHold,
        lastUpdated: serverTimestamp(),
      });

      // Also update the onHold count for the brand
      const brandRef = doc(db, "inventory", `${item.name.replace(/ /g, "_")}__onHold`);
      const brandDoc = await getDoc(brandRef);
      const currentBrandOnHold = brandDoc.data()?.onHold || 0;

      await updateDoc(brandRef, {
        onHold: currentBrandOnHold + 1,
        lastUpdated: serverTimestamp(),
      });

      // Log the hold action
      await addDoc(collection(db, "logs"), {
        user: fullName || user.email,
        action: "Put on Hold",
        brand: item.name || "Unknown",
        section: isBackdoor ? "backdoor" : item.section || "Unknown",
        model: model.trim() || "Unknown",
        reason: reason.trim() || "No reason provided",
        timestamp: serverTimestamp(),
      });

      // Update local UI
      setInventory((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                visible: newVisible,
                backdoor: newBackdoor,
                onHold: newOnHold,
              }
            : i
        )
      );

      // Show success checkmark
      setStatus("success");
      setTimeout(() => setShowModal(false), 1500);
    } catch (error) {
      console.error("❌ Error placing item on hold:", error);
      setStatus("error");
      setTimeout(() => setStatus(null), 2000);
    }
  };

  return (
    <div className="modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
        <h2 className="text-lg font-semibold mb-4">Hold Item</h2>
        <p>Placing <strong>{item.name}</strong> on hold.</p>

        <label className="block mb-2">
          Model (optional):
          <input
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full border p-2 rounded mb-3"
            placeholder="Enter model"
          />
        </label>

        <label className="block mb-2">
          Reason (optional):
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border p-2 rounded mb-3"
            placeholder="Enter reason"
          />
        </label>

        <button onClick={handleSubmit} className="bg-green-600 text-white py-2 px-4 rounded w-full mb-2">
          Confirm Hold
        </button>
        <button onClick={() => setShowModal(false)} className="bg-gray-500 text-white py-2 px-4 rounded w-full">
          Cancel
        </button>

        {/* Success/Failure Indicators */}
        {status === "success" && <div className="text-green-500 text-center mt-4">✅ Item successfully placed on hold!</div>}
        {status === "error" && <div className="text-red-500 text-center mt-4">❌ Error placing item on hold</div>}
      </div>
    </div>
  );
};

export default HoldModal;